import React, { useCallback, useMemo, useRef } from 'react';

import { Skeleton, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { COLLABORATOR_STATUS } from '../../../../../../../constants/collaborators';
import { WHITE } from '../../../../../../../constants/colors';
import { CLOSE_ICON, DELETE_V3, DOWNLOAD_SIMPLE, EMAIL } from '../../../../../../../constants/images';
import useCollaboratorRFData from '../../../../../../../hooks/useCollaboratorRFData/useCollaboratorRFData';
import useOutsideComponentClick from '../../../../../../../hooks/useOutsideComponentClick/outsideComponentClick';
import { IMembersData } from '../../../../../../../interfaces/businessData';
import { IBdsIconButtonImage } from '../../../../../../../interfaces/buttons';
import { IInsuranceCertificate } from '../../../../../../../interfaces/collaborators';
import {
  CERTIFICATE_TYPE_TRANSLATIONS,
  TRANSLATION_COLLABORATORS
} from '../../../../../../../translations/collaborators';
import { GenericVoidFunction } from '../../../../../../../types/genericTypes';
import BdsIconButton from '../../../../../Buttons/BdsIconButton/BdsIconButton';
import BdsDivider from '../../../../../Globals/BdsDivider/BdsDivider';
import './CollaboratorsActionsMenu.scss';

interface ICollaboratorsActionsMenuProps {
  showMenu: boolean;
  setShowMenu: GenericVoidFunction;
  member: IMembersData;
  companyId: string;
  hasNovationOption: boolean;
  hasInsuranceCertificate: boolean;
  onOptionClick: GenericVoidFunction;
  onCertificateDownload: (certificate: IInsuranceCertificate) => void;
  className?: string;
  loadingCertificates?: boolean;
  certificates?: IInsuranceCertificate[];
  disableOutsideClick?: boolean;
}

interface MenuItemProps {
  title?: string;
  label: string;
  icon: IBdsIconButtonImage;
  onClick: GenericVoidFunction;
  disabled?: boolean;
  showDivider?: boolean;
  disabledMessage?: string;
  loading?: boolean;
}

const MenuItem = ({
  title,
  label,
  icon,
  onClick,
  disabled = false,
  showDivider = true,
  disabledMessage,
  loading = false
}: MenuItemProps) => {
  const IconComponent = icon.component;
  return (
    <div className="cam-dropdown" role="menuitem">
      {title && <h3 className="cam-dropdown-title">{title}</h3>}
      <button className="cam-lc-item dropdown" onClick={onClick} disabled={disabled} aria-label={label}>
        <div className="item-label">
          <div className="cam-texts">
            <p className={disabled ? 'disabled' : ''}>{label}</p>
            {disabled && disabledMessage && <p className="disabled-agreement">{disabledMessage}</p>}
          </div>
          {loading ? (
            <CircularProgress sx={{ color: WHITE }} size={24} />
          ) : (
            <div className={`item-icon ${disabled ? 'disabled' : ''}`}>
              <IconComponent />
            </div>
          )}
        </div>
      </button>
      {showDivider && <BdsDivider size="s" color="high" />}
    </div>
  );
};

const CollaboratorsActionsMenu = ({
  showMenu,
  setShowMenu,
  member,
  companyId,
  hasNovationOption,
  hasInsuranceCertificate,
  loadingCertificates,
  certificates = [],
  onOptionClick,
  onCertificateDownload,
  className = '',
  disableOutsideClick = false
}: ICollaboratorsActionsMenuProps) => {
  const { t } = useTranslation();
  const menuRef = useRef<HTMLDivElement>(null);

  const { rfDataState } = useCollaboratorRFData(companyId, member.email, hasNovationOption);

  const handleCloseMenu = useCallback(() => {
    setShowMenu(false);
  }, [setShowMenu]);

  useOutsideComponentClick(menuRef, handleCloseMenu, !disableOutsideClick);

  const menuItems = useMemo(() => {
    const items: MenuItemProps[] = [
      {
        label: t(TRANSLATION_COLLABORATORS.table.menu.deleteSubscription),
        icon: DELETE_V3,
        onClick: () => onOptionClick('delete')
      }
    ];

    if (member.status === 'invited') {
      items.push({
        title: t(TRANSLATION_COLLABORATORS.table.menu.resendEmailLabel),
        label: t(TRANSLATION_COLLABORATORS.table.menu.resendEmail),
        icon: EMAIL,
        onClick: () => onOptionClick('resendEmail')
      });
    }

    if (hasNovationOption && !rfDataState.isLoading) {
      items.push({
        title: t(TRANSLATION_COLLABORATORS.table.menu.novation.documents),
        label: t(TRANSLATION_COLLABORATORS.table.menu.novation.main),
        icon: DOWNLOAD_SIMPLE,
        onClick: () => onOptionClick('novation'),
        disabled: !rfDataState.data?.hasNovation || member.status !== COLLABORATOR_STATUS.active,
        showDivider: false,
        disabledMessage: t(TRANSLATION_COLLABORATORS.table.menu.novation.agreementNotAvailable)
      });
    }

    if (hasInsuranceCertificate && !loadingCertificates) {
      certificates.forEach((cert, index) => {
        const isLast = index === certificates.length - 1;
        items.push({
          title: index === 0 ? t(TRANSLATION_COLLABORATORS.table.menu.insuranceCertificate.documents) : undefined,
          label: t(CERTIFICATE_TYPE_TRANSLATIONS[cert.type] || cert.type),
          icon: DOWNLOAD_SIMPLE,
          onClick: () => onCertificateDownload(cert),
          showDivider: !isLast
        });
      });
    }

    return items;
  }, [
    t,
    member.status,
    hasNovationOption,
    hasInsuranceCertificate,
    rfDataState,
    onOptionClick,
    loadingCertificates,
    certificates,
    onCertificateDownload
  ]);

  return (
    <div
      ref={menuRef}
      aria-hidden={!showMenu}
      className={`generic-menu-container ${className} ${showMenu ? 'displayed' : ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="gm-top">
        <div className="gm-top icon-close-top dropdown">
          <BdsIconButton icon={CLOSE_ICON} action={handleCloseMenu} variant="transparent" size="sm" />
        </div>
      </div>

      <div className="gm-center">
        <div className="cam-list-content dropdown">
          {menuItems.map((item) => (
            <MenuItem key={uuid()} {...item} />
          ))}

          {hasNovationOption && rfDataState.isLoading && (
            <div className="cam-dropdown">
              <h3 className="cam-dropdown-title">{t(TRANSLATION_COLLABORATORS.table.menu.novation.documents)}</h3>
              <div className="dropdown">
                <Skeleton className="cam-dropdown-skeleton" variant="rectangular" width="100%" height={79} />
              </div>
            </div>
          )}

          {hasInsuranceCertificate && loadingCertificates && (
            <div className="cam-dropdown">
              <h3 className="cam-dropdown-title">
                {t(TRANSLATION_COLLABORATORS.table.menu.insuranceCertificate.documents)}
              </h3>
              <div className="dropdown">
                <Skeleton className="cam-dropdown-skeleton" variant="rectangular" width="100%" height={79} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(CollaboratorsActionsMenu);
